/**
 * Which canvas a piece of content belongs to.
 *
 * Canvas writes are per-persona: ToolCanvas filters tabs by `targetTool`, so a
 * tab owned by Concierge does not render in the Study Design canvas. Ownership
 * is therefore a fact about the write, decided once when `add_to_canvas` runs.
 *
 * It used to be re-derived on every read from `targetToolForMode(modeRef
 * .current)` — the persona you happen to be looking from. That is correct only
 * while you stay in the workspace that did the writing, and `route_handoff`
 * exists precisely to take you somewhere else with the same session. After a
 * handoff, every add_to_canvas message in the conversation renamed itself after
 * the new persona and linked to that persona's canvas, which filters the
 * content out. Following the link landed on an empty workspace — the content
 * looked lost, and was not.
 *
 * So the owner is written down at persist time and read back here.
 *
 * "Multiple personas' tools in one turn" doesn't need a tie-break: a chat
 * session's mode (and therefore its tool suppression set — see MODE_CONFIGS
 * in prompts/routing.ts) is fixed for the session's lifetime, so every tool
 * visible to the model in a given `add_to_canvas` call already belongs to
 * exactly one persona. Adding a "count tool calls, pick the majority" step
 * would be exactly the kind of re-derivation this file exists to avoid —
 * it would need to run at read time (ownership isn't known until the write
 * happens) and would reintroduce the class of bug described above.
 */
import type { ToolId } from './prompts/canvas-meta'

/**
 * The canvases that exist.
 *
 * Source of truth for the *values*: `Crucible-legacy/shared/canvas-owners.json`.
 * This array must match that file exactly — enforced by
 * `canvas-owners-parity.test.ts` (frontend) and the backend sessions unit test.
 */
export const CANVAS_OWNERS_LIST = [
  'concierge',
  'study-design',
  'norma',
  'val',
  'testcases',
  'marq',
  'compli',
] as const satisfies readonly ToolId[]

const CANVAS_OWNERS: ReadonlySet<string> = new Set(CANVAS_OWNERS_LIST)

/** The one place a stored string is trusted to name a canvas. */
export function isCanvasOwner(value: unknown): value is ToolId {
  return typeof value === 'string' && CANVAS_OWNERS.has(value)
}

/**
 * The canvas that owns this content.
 *
 * `toolResult` is `unknown` because that is what it is: an arbitrary persisted
 * tool-result blob, read back from a conversation row written by a version of
 * the app that may not have recorded `targetTool` at all. Declaring it as
 * `{ targetTool?: unknown }` was worse than useless — every property is
 * optional, so TypeScript's weak-type rule *rejected* exactly the legacy
 * results this function exists to handle (a `show_form_preview` result, say,
 * shares no property with it), while admitting nothing it could actually check.
 *
 * `viewerOwner` is used only for messages persisted before the owner was
 * recorded. That information was never captured for them and cannot be
 * recovered here, so they keep the old guess rather than gaining a wrong
 * certainty — legacy conversations behave exactly as they do today.
 */
export function canvasOwnerFor(toolResult: unknown, viewerOwner: ToolId): ToolId {
  if (typeof toolResult !== 'object' || toolResult === null || !('targetTool' in toolResult)) {
    return viewerOwner
  }
  const { targetTool } = toolResult
  return isCanvasOwner(targetTool) ? targetTool : viewerOwner
}

/**
 * Resolve where a ContentTab should land when its stored `targetTool` may be
 * missing.
 *
 * Null / undefined / empty means *unspecified*, not Study Design. Explicit
 * owners are never rewritten. When unspecified:
 *   1. Prefer the workspace the user is currently in (`activeWorkspace`)
 *   2. Otherwise land on Concierge — the historical home for untagged canvas
 *      content before per-persona tagging existed
 *
 * Used by ToolCanvas filtering, canvas-library stamping, and any other read
 * path that used to coerce null → `'study-design'`.
 */
export function resolveTabTargetTool(
  tabTargetTool: string | null | undefined,
  activeWorkspace?: ToolId | null,
): ToolId {
  if (isCanvasOwner(tabTargetTool)) return tabTargetTool
  if (activeWorkspace && isCanvasOwner(activeWorkspace)) return activeWorkspace
  return 'concierge'
}

/**
 * True when a tab should render on the given ToolCanvas mount.
 *
 * `activeWorkspace` is the persona the user is actually in (from shell / chat
 * mode). It must NOT default to `canvasTool` — doing so made every concurrent
 * ToolCanvas mount claim the same untagged legacy tab. Untagged tabs resolve
 * via {@link resolveTabTargetTool}: active workspace if provided, else Concierge.
 */
export function tabBelongsToTool(
  tabTargetTool: string | null | undefined,
  canvasTool: ToolId,
  activeWorkspace?: ToolId | null,
): boolean {
  return resolveTabTargetTool(tabTargetTool, activeWorkspace ?? null) === canvasTool
}
